import {
  Input,
  Box,
  Text,
  VStack,
  Button,
  Image as ChakraImage,
  HStack,
  Textarea,
  Stack,
  Divider,
  Icon,
  useMediaQuery,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import {
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import transform from "./transform/adcreative";
import { StepProps } from "./types";
import WhiteBgInput from "./WhiteBgInput";
import ValidatedField from "./ValidatedField";
import { AdCreativeFormData, AdCreativeFormSchema } from "./forms/adcreative";
import { yupResolver } from "@hookform/resolvers/yup";
import { InputLabel } from "./AdSetForm";
import InfoSection from "src/ui/InfoSection";
import { FaInfo } from "react-icons/fa";
import TabSelector from "./components/TabSelector";
import AdPreview from "./components/AdPreview/AdPreview";
import { SingleTab, CarouselTab } from "./components/tabs";
import { S3Response } from "react-s3-uploader";
import actions from "../../../../../actions";
import { ErrorMessage } from "@hookform/error-message";
import PlacementSelector, {
  PlacementOption,
} from "./components/PlacementSelector";
import {
  FacebookPositions,
  InstagramPositions,
} from "common/interfaces/KYDMarketing";
import ErrorBoundary from "src/Manager/EventDetail/sections/ErrorBoundary";
import { generateCallToActionOptions } from "src/utils/callToActionOptions";

const ShowWhen = ({
  when,
  children,
}: {
  when: boolean;
  children?: React.ReactNode;
}): JSX.Element | null => {
  return when ? <>{children}</> : null;
};

const AdCreativeForm = forwardRef<ReturnType<typeof useForm<any>>, StepProps>(
  (
    {
      ad,
      adset,
      data,
      campaign,
      instagramAccounts = [],
      pages,
      loading: _outsideLoading,
    },
    ref
  ) => {
    const [isWebsiteEditing, setIsWebsiteEditing] = useState(false);
    const [isPlacementEditing, setIsPlacementEditing] = useState(false);
    const [tab, setTab] = useState<"creative" | "preview">("creative");

    const callToActionOptions = useMemo(() => {
      return generateCallToActionOptions(campaign.campaign_type);
    }, []);

    const form = useForm<AdCreativeFormData>({
      resolver: yupResolver(AdCreativeFormSchema),
      mode: "onTouched",
      criteriaMode: "all",
      reValidateMode: "onChange",
      shouldFocusError: true,
      delayError: 0,
      values: transform.input({
        link: data?.link,
        ad,
        campaign,
        adset,
        pages,
        instagramAccounts,
        callToActionOptions,
      }),
    });

    const websiteUrl = useWatch({
      control: form.control,
      name: "website_url",
    });

    const createPublicUploadUrl = useCallback(
      async ({
        file,
        callback,
      }: {
        file: File;
        callback: (params: S3Response) => void | Promise<void | string>;
      }) => {
        const response = await actions.ADMIN.createPublicUploadUrl(
          file,
          callback
        );
        return response as unknown as S3Response & { cdn_url: string };
      },
      []
    );

    const {
      register,
      control,
      formState: { errors },
      trigger,
      watch,
      setValue,
    } = form;

    // Expose form methods to parent component through ref
    useImperativeHandle(ref, () => form);

    // Trigger validation on mount and when media type changes
    useEffect(() => {
      trigger();
    }, [trigger, watch("media_type"), watch("images"), watch("videos")]);

    const media_type = watch("media_type");

    const isLoading =
      _outsideLoading.adsets ||
      _outsideLoading.campaigns ||
      _outsideLoading.audiences ||
      _outsideLoading.pixels ||
      _outsideLoading.publish ||
      _outsideLoading.pages ||
      _outsideLoading.instagramAccounts;

    const [isMobile] = useMediaQuery("(max-width: 768px)");

    // Placement data
    const facebookPlacementOptions: PlacementOption[] = [
      {
        label: "Facebook Feed",
        value: FacebookPositions.feed,
        type: "facebook",
      },
      {
        label: "Facebook Video Feeds",
        value: FacebookPositions.video_feeds,
        type: "facebook",
      },
      {
        label: "Facebook Reels",
        value: FacebookPositions.facebook_reels,
        type: "facebook",
      },
      {
        label: "Facebook Reels Overlay",
        value: FacebookPositions.facebook_reels_overlay,
        type: "facebook",
      },
      {
        label: "Facebook In-Stream Video",
        value: FacebookPositions.instream_video,
        type: "facebook",
      },
      {
        label: "Facebook Story",
        value: FacebookPositions.story,
        type: "facebook",
      },
      {
        label: "Facebook Profile Feed",
        value: FacebookPositions.profile_feed,
        type: "facebook",
      },
      {
        label: "Marketplace",
        value: FacebookPositions.marketplace,
        type: "facebook",
      },
    ];

    const instagramPlacementOptions: PlacementOption[] = [
      {
        label: "Instagram Feed",
        value: InstagramPositions.stream,
        type: "instagram",
      },
      {
        label: "Instagram Reels",
        value: InstagramPositions.reels,
        type: "instagram",
      },
      {
        label: "Instagram Story",
        value: InstagramPositions.story,
        type: "instagram",
      },
      {
        label: "Instagram Explore",
        value: InstagramPositions.explore,
        type: "instagram",
      },
      {
        label: "Instagram Explore Home",
        value: InstagramPositions.explore_home,
        type: "instagram",
      },
      {
        label: "Instagram Profile Feed",
        value: InstagramPositions.profile_feed,
        type: "instagram",
      },
      {
        label: "Instagram Search",
        value: InstagramPositions.ig_search,
        type: "instagram",
      },
    ];

    // Get selected placements from form
    // Use nullish coalescing to distinguish between undefined (use defaults) and empty array (no selections)
    const watchedFacebook = watch("facebook_placements");
    const watchedInstagram = watch("instagram_placements");
    const facebookPlacements = watchedFacebook ?? [
      FacebookPositions.feed,
      FacebookPositions.facebook_reels,
      FacebookPositions.facebook_reels_overlay,
      FacebookPositions.story,
    ];
    const instagramPlacements = watchedInstagram ?? [
      InstagramPositions.story,
      InstagramPositions.reels,
    ];

    const handlePlacementChange = (placements: {
      facebook: string[];
      instagram: string[];
    }) => {
      setValue("facebook_placements", placements.facebook, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("instagram_placements", placements.instagram, {
        shouldDirty: true,
        shouldValidate: true,
      });
    };

    const selectedPlacementLabels = useMemo(() => {
      const fbLabels = facebookPlacements.map(
        (value) =>
          facebookPlacementOptions.find((o) => o.value === value)?.label ||
          value
      );
      const igLabels = instagramPlacements.map(
        (value) =>
          instagramPlacementOptions.find((o) => o.value === value)?.label ||
          value
      );
      return [...fbLabels, ...igLabels].join(", ");
    }, [facebookPlacements, instagramPlacements]);

    const handlePlacementSave = () => {
      setIsPlacementEditing(false);
    };

    const isIdentityCompleted = useMemo(() => {
      return (
        !!form.watch("page_id")?.value &&
        !!form.watch("instagram_actor_id")?.value
      );
    }, [form.watch("page_id")?.value, form.watch("instagram_actor_id")?.value]);

    const isMediaCompleted = useMemo(() => {
      const mediaType = form.watch("media_type");
      const carouselMediaType = form.watch("carousel_media_type");
      const images = form.watch("images");
      const videos = form.watch("videos");
      const message = form.watch("message");
      const callToAction = form.watch("call_to_action");

      if (!mediaType || !message || !callToAction?.value) {
        return false;
      }

      // For carousel ads, check based on carousel_media_type
      if (mediaType === "carousel") {
        if (carouselMediaType === "photo") {
          return !!images?.portrait?.length;
        } else if (carouselMediaType === "video") {
          return !!(videos?.portrait?.length && videos.portrait[0]?.source);
        }
        return false;
      }

      // For single ads, check if either images or videos are present (both formats required)
      if (mediaType === "single") {
        const hasImages =
          !!images?.portrait?.length &&
          !!images?.square?.length &&
          !!images.portrait[0]?.picture_url &&
          !!images.square[0]?.picture_url;
        const hasVideos =
          !!videos?.portrait?.length &&
          !!videos?.square?.length &&
          !!videos.portrait[0]?.source &&
          !!videos.square[0]?.source;
        return hasImages || hasVideos;
      }

      return false;
    }, [
      form.watch("media_type"),
      form.watch("carousel_media_type"),
      form.watch("message"),
      form.watch("images"),
      form.watch("videos"),
      form.watch("call_to_action"),
    ]);

    return (
      <FormProvider {...form}>
        <Stack
          w="full"
          h="full"
          alignItems="stretch"
          gap={1}
          direction={["column", "row"]}
          flexGrow={1}
        >
          <ShowWhen when={isMobile}>
            <Box p={2}>
              <TabSelector
                options={[
                  {
                    id: "creative",
                    label: "Creative",
                    content: <></>,
                  },
                  {
                    id: "preview",
                    label: "Preview",
                    content: <></>,
                  },
                ]}
                onOptionChange={(optionId) => {
                  setTab(optionId as "creative" | "preview");
                }}
                display={{ base: "block", md: "none" }}
              />
            </Box>
          </ShowWhen>
          <ShowWhen when={!isMobile || (isMobile && tab === "creative")}>
            <VStack
              textAlign={"left"}
              gap={[2, 4]}
              flex={1}
              minH="full"
              p={[2, 4]}
              px={4}
              w="full"
            >
              <Text
                w="full"
                fontSize={["lg", "xl"]}
                fontWeight={"semibold"}
                textAlign={"left"}
              >
                New creative
              </Text>
              <VStack w="full" spacing={1}>
                <InputLabel>Ad name</InputLabel>
                <WhiteBgInput
                  placeholder="Image 1"
                  {...register(`name`, { required: true })}
                />
                <ErrorMessage
                  name={`name`}
                  errors={errors}
                  render={({ message }) => (
                    <Text pt={2} fontSize="sm" color="kydRed.200">
                      {message}
                    </Text>
                  )}
                />
              </VStack>
              <Divider borderColor={"gray.200"} />

              <InfoSection
                title="Identity"
                isCompleted={isIdentityCompleted}
                description="Choose the facebook page and instagram account the ad will be associated with"
              />

              <Box
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
                p={[2, 4]}
                w="full"
                bg="white"
              >
                <VStack w="full" spacing={2} alignItems={"flex-start"}>
                  <ValidatedField errors={errors} name="page_id">
                    <VStack w="full" spacing={2} alignItems={"flex-start"}>
                      <Text fontWeight={"bold"} fontSize={"sm"}>
                        Facebook page
                      </Text>
                      <Controller
                        control={control}
                        name="page_id"
                        render={({ field }) => (
                          <Select
                            isDisabled={isLoading}
                            value={field.value}
                            onChange={(values) => field.onChange(values)}
                            chakraStyles={{
                              container: () => ({
                                position: "relative",
                                width: "100%",
                                backgroundColor: "white",
                              }),
                            }}
                            // @ts-ignore
                            options={pages.map((g) => ({
                              label: g.name,
                              value: g.id,
                            }))}
                          />
                        )}
                      />
                    </VStack>
                  </ValidatedField>
                  <ValidatedField errors={errors} name="instagram_actor_id">
                    <VStack w="full" spacing={2} alignItems={"flex-start"}>
                      <Text fontWeight={"bold"} fontSize={"sm"}>
                        Instagram account
                      </Text>
                      <Controller
                        control={control}
                        name="instagram_actor_id"
                        render={({ field }) => (
                          <Select
                            isDisabled={isLoading}
                            value={field.value}
                            onChange={(values) => field.onChange(values)}
                            chakraStyles={{
                              container: () => ({
                                position: "relative",
                                width: "100%",
                                backgroundColor: "white",
                              }),
                            }}
                            // @ts-ignore
                            options={instagramAccounts.map((g) => ({
                              label: g.username,
                              value: g.id,
                            }))}
                          />
                        )}
                      />
                    </VStack>
                  </ValidatedField>
                </VStack>
              </Box>
              <InfoSection
                title="Ad creative"
                description="Select and optimize your ad text, media and enhancements"
                isCompleted={isMediaCompleted}
              />

              <TabSelector
                size="md"
                w="full"
                options={[
                  {
                    id: "single",
                    label: "Single",
                    content: (
                      <SingleTab
                        createPublicUploadUrl={createPublicUploadUrl}
                      />
                    ),
                  },
                  {
                    id: "carousel",
                    label: "Carousel",
                    content: (
                      <CarouselTab
                        createPublicUploadUrl={createPublicUploadUrl}
                      />
                    ),
                  },
                ]}
                optionId={media_type === "carousel" ? "carousel" : "single"}
                onOptionChange={(optionId) => {
                  if (optionId === "single") {
                    setValue("media_type", "single"); // Default to photo for single
                    setValue("carousel_media_type", null);
                    setValue("images.portrait", []);
                    setValue("videos.portrait", []);
                    setValue("images.square", []);
                    setValue("videos.square", []);
                  } else if (optionId === "carousel") {
                    setValue("media_type", "carousel");
                    setValue("carousel_media_type", "photo"); // Default to photo for media list
                    setValue("images.portrait", []);
                    setValue("videos.portrait", []);
                    setValue("images.square", []);
                    setValue("videos.square", []);
                  }
                }}
              />
              <VStack
                w="full"
                bg="white"
                rounded="xl"
                p={[2, 4]}
                spacing={2}
                border="1px solid"
                borderColor="gray.200"
                alignItems={"flex-start"}
              >
                <HStack w="full" justifyContent={"space-between"}>
                  <Text fontWeight={"bold"} fontSize={"sm"}>
                    Primary text
                  </Text>
                  <Text fontSize={"xs"} color="gray.500">
                    0/125 characters
                  </Text>
                </HStack>
                <Textarea
                  placeholder="Ad text"
                  {...register(`message`, { required: true })}
                />
                <ErrorMessage
                  name={`message`}
                  errors={errors}
                  render={({ message }) => (
                    <Text pt={2} fontSize="sm" color="kydRed.200">
                      {message}
                    </Text>
                  )}
                />
              </VStack>

              <VStack
                w="full"
                bg="white"
                rounded="xl"
                p={[2, 4]}
                spacing={2}
                border="1px solid"
                borderColor="gray.200"
                alignItems={"flex-start"}
              >
                <HStack w="full" justifyContent={"space-between"}>
                  <Text fontWeight={"bold"} fontSize={"sm"}>
                    Call to action
                  </Text>
                </HStack>
                <Controller
                  control={control}
                  name="call_to_action"
                  render={({ field }) => (
                    <Select
                      options={callToActionOptions}
                      defaultValue={callToActionOptions.find(
                        (cta) => cta.value === field.value.value
                      )}
                      value={field.value}
                      onChange={(values) => field.onChange(values)}
                      placeholder="Select call to action"
                      chakraStyles={{
                        container: () => ({
                          position: "relative",
                          width: "100%",
                          backgroundColor: "white",
                        }),
                      }}
                    />
                  )}
                />
              </VStack>

              <Divider borderColor={"gray.200"} />
              <Divider borderColor={"gray.200"} />
              <InfoSection
                title="Placement and destination"
                isCompleted={true}
                description="We'll automatically show ads in the places where people are likely to respond"
                rightIcon={<Icon as={FaInfo} fontSize={32} color="gray.400" />}
              />
              {isPlacementEditing ? (
                <Box
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  p={4}
                  bg="white"
                  w="full"
                >
                  <PlacementSelector
                    facebookPlacements={facebookPlacementOptions}
                    instagramPlacements={instagramPlacementOptions}
                    selectedFacebook={facebookPlacements}
                    selectedInstagram={instagramPlacements}
                    onPlacementChange={handlePlacementChange}
                    onSave={handlePlacementSave}
                  />
                </Box>
              ) : (
                <Box
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  p={4}
                  bg="white"
                  w="full"
                >
                  <HStack w="full" justifyContent="space-between">
                    <VStack alignItems={"flex-start"} spacing={0}>
                      <Text fontWeight={"semibold"}>Placement Controls</Text>
                      <Text fontSize={"sm"} color={"gray.500"}>
                        {selectedPlacementLabels || "None selected"}
                      </Text>
                    </VStack>
                    <Button
                      variant={"outline"}
                      size={"sm"}
                      onClick={() => setIsPlacementEditing(true)}
                    >
                      Edit
                    </Button>
                  </HStack>
                </Box>
              )}
              <VStack
                w="full"
                bg="white"
                rounded="xl"
                p={[2, 4]}
                spacing={2}
                border="1px solid"
                borderColor="gray.200"
                alignItems={"flex-start"}
              >
                <HStack w="full" justifyContent={"space-between"}>
                  <VStack alignItems={"flex-start"} spacing={0}>
                    <Text fontWeight={"semibold"}>Website</Text>
                    <Text fontSize={"xs"} color="gray.500" noOfLines={1}>
                      {websiteUrl ||
                        "By default we send people to the event page"}
                    </Text>
                  </VStack>
                  <Button
                    variant={"outline"}
                    size={"sm"}
                    flexShrink={0}
                    onClick={() => setIsWebsiteEditing(!isWebsiteEditing)}
                  >
                    {isWebsiteEditing ? "Save" : "Edit"}
                  </Button>
                </HStack>

                {isWebsiteEditing ? (
                  <VStack w="full" spacing={2}>
                    <Input
                      placeholder="Enter website URL (e.g., https://example.com)"
                      defaultValue={data?.link}
                      {...register("website_url")}
                      size="sm"
                    />
                    <HStack w="full" justifyContent="flex-end" spacing={2}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsWebsiteEditing(false)}
                      >
                        Cancel
                      </Button>
                    </HStack>
                  </VStack>
                ) : null}
              </VStack>
            </VStack>
          </ShowWhen>

          <ShowWhen when={(isMobile && tab === "preview") || !isMobile}>
            <ErrorBoundary>
              <AdPreview
                h="full"
                w={["full", "50%"]}
                instagramAccounts={instagramAccounts}
                pages={pages}
                roundedRight="md"
                name={data?.name}
                description={data?.description}
              />
            </ErrorBoundary>
          </ShowWhen>
        </Stack>
      </FormProvider>
    );
  }
);

export default AdCreativeForm;
